'use client';

import Link from 'next/link';
import { useRouter } from 'next/navigation';
import * as React from 'react';
import { useQuery } from '@tanstack/react-query';

import { PageContainer } from '@/components/layout/page-container';
import { PageHeader } from '@/components/layout/page-header';
import { Card } from '@/components/ui/card';
import { Money } from '@/components/ui/money';
import { Skeleton } from '@/components/ui/skeleton';
import { Stat, StatStrip } from '@/components/ui/stat-card';
import { listAuditEvents } from '@/lib/api/admin-audit';
import { getFleetSummary, type FleetCard } from '@/lib/api/admin-fleet-summary';
import { getObservability } from '@/lib/api/admin-observability';
import { getHardFreeze, getLiveTrading, hardFreezeQueryKey, platformLiveQueryKey } from '@/lib/api/admin-platform';
import { listDeployments, type StrategyDeployment } from '@/lib/api/admin-strategy-deployments';
import { listAdminTrades } from '@/lib/api/admin-trades';
import { useFormatTime } from '@/lib/display-tz';
import { cn } from '@/lib/util/cn';

const humanize = (s: string | null | undefined): string =>
  s ? (s.charAt(0).toUpperCase() + s.slice(1)).replace(/[-_]/g, ' ') : '';

function ago(iso: string | null | undefined): string {
  if (!iso) {return '—';}
  const s = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (!Number.isFinite(s) || s < 0) {return '—';}
  if (s < 90) {return `${s}s`;}
  if (s < 5400) {return `${Math.round(s / 60)}m`;}
  if (s < 172_800) {return `${Math.round(s / 3600)}h`;}
  return `${Math.round(s / 86_400)}d`;
}

type Tone = 'run' | 'halt' | 'fail' | 'idle';
function deploymentState(d: StrategyDeployment): { label: string; tone: Tone; detail?: string | undefined } {
  if (d.risk_halted_at) {return { label: 'Halted', tone: 'halt', detail: d.risk_halt_reason ?? undefined };}
  if (d.status === 'failed') {return { label: 'Failed', tone: 'fail', detail: d.last_error ?? undefined };}
  if (d.status === 'running') {
    return { label: d.scheduler_state ? humanize(d.scheduler_state) : 'Running', tone: 'run', detail: d.scheduler_detail ?? undefined };
  }
  return { label: d.scheduler_state ? humanize(d.scheduler_state) : 'Stopped', tone: 'idle', detail: d.scheduler_detail ?? undefined };
}
const TONE_ORDER: Record<Tone, number> = { halt: 0, fail: 1, run: 2, idle: 3 };
const TONE_CLASS: Record<Tone, string> = {
  run: 'text-success',
  halt: 'text-short',
  fail: 'text-short',
  idle: 'text-fg-subtle',
};

export default function ControlCenterPage(): React.ReactElement {
  const router = useRouter();
  const fmtTime = useFormatTime();
  const sinceIso = React.useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d.toISOString();
  }, []);

  const depQ = useQuery({
    queryKey: ['deployments'],
    queryFn: ({ signal }) => listDeployments({}, signal),
    refetchInterval: 5000,
  });
  const fleetQ = useQuery({
    // The endpoint caps cards at 50; `totals` always cover the whole fleet.
    queryKey: ['fleet-summary', sinceIso, 50],
    queryFn: ({ signal }) => getFleetSummary({ since: sinceIso, limit: 50 }, signal),
    refetchInterval: 5000,
  });
  const obsQ = useQuery({
    queryKey: ['observability'],
    queryFn: ({ signal }) => getObservability(signal),
    refetchInterval: 10_000,
  });
  const liveQ = useQuery({ queryKey: platformLiveQueryKey, queryFn: ({ signal }) => getLiveTrading(signal) });
  const freezeQ = useQuery({ queryKey: hardFreezeQueryKey, queryFn: ({ signal }) => getHardFreeze(signal) });
  const tradesQ = useQuery({
    queryKey: ['control-trades', sinceIso],
    queryFn: ({ signal }) => listAdminTrades({ since: sinceIso, limit: 50 }, signal),
    refetchInterval: 5000,
  });
  const auditQ = useQuery({
    queryKey: ['control-audit'],
    queryFn: ({ signal }) => listAuditEvents({ limit: 60 }, signal),
    refetchInterval: 15_000,
  });

  const deployments = React.useMemo(() => depQ.data?.data ?? [], [depQ.data]);
  const fleet = fleetQ.data?.data ?? null;
  const cardsById = React.useMemo(() => {
    const m = new Map<string, FleetCard>();
    for (const c of fleet?.cards ?? []) {m.set(c.deployment_id, c);}
    return m;
  }, [fleet]);
  const rows = React.useMemo(
    () =>
      deployments
        .map((d) => ({ d, state: deploymentState(d), card: cardsById.get(d.id) }))
        .sort(
          (a, b) =>
            TONE_ORDER[a.state.tone] - TONE_ORDER[b.state.tone] ||
            (a.d.strategy_code_name ?? a.d.strategy_id).localeCompare(b.d.strategy_code_name ?? b.d.strategy_id),
        ),
    [deployments, cardsById],
  );
  const counts = rows.reduce(
    (acc, r) => ({ ...acc, [r.state.tone]: acc[r.state.tone] + 1 }),
    { run: 0, halt: 0, fail: 0, idle: 0 } as Record<Tone, number>,
  );

  const brokers = obsQ.data?.data.brokers ?? [];
  const orders = brokers.reduce((n, b) => n + b.orders, 0);
  const errors = brokers.reduce((n, b) => n + b.errors, 0);
  const p95s = brokers.map((b) => b.order_latency_ms.p95).filter((x): x is number => x != null);
  const p95 = p95s.length ? Math.max(...p95s) : null;

  const liveOn = liveQ.data?.data.enabled ?? null;
  const frozen = freezeQ.data?.data.enabled ?? null;

  const alerts: { key: string; tone: 'crit' | 'warn'; text: React.ReactNode; href?: string }[] = [];
  if (frozen) {alerts.push({ key: 'freeze', tone: 'crit', text: 'Hard freeze is on — every order is blocked, including exits.' });}
  if (liveOn === false && counts.run > 0) {
    alerts.push({ key: 'live', tone: 'warn', text: `Live trading is off while ${counts.run} deployment${counts.run === 1 ? ' is' : 's are'} running.` });
  }
  for (const r of rows) {
    if (r.state.tone === 'halt' || r.state.tone === 'fail') {
      alerts.push({
        key: r.d.id,
        tone: 'crit',
        href: `/strategies/${r.d.id}`,
        text: (
          <>
            <span className="font-medium">{r.d.strategy_code_name ?? r.d.strategy_id}</span> {r.state.label.toLowerCase()}
            {r.state.detail ? <span className="text-fg-subtle">: {r.state.detail}</span> : null}
          </>
        ),
      });
    }
  }
  for (const b of brokers) {
    if (b.circuit !== 'closed') {
      alerts.push({ key: `cb-${b.broker}`, tone: 'crit', text: `${b.broker}: order circuit ${b.circuit.replace('_', '-')} after ${b.consecutive_failures} failures.` });
    } else if (b.orders >= 20 && b.error_rate > 0.05) {
      alerts.push({ key: `er-${b.broker}`, tone: 'warn', text: `${b.broker}: ${(b.error_rate * 100).toFixed(1)}% of orders erroring${b.last_error ? ` — ${b.last_error}` : ''}.` });
    }
  }

  const events = React.useMemo(() => {
    const out: { at: string; key: string; text: React.ReactNode }[] = [];
    for (const t of tradesQ.data?.data ?? []) {
      if (t.entry_time) {
        out.push({
          at: t.entry_time,
          key: `e-${t.id}`,
          text: (
            <>
              <span className={t.side.toLowerCase() === 'buy' ? 'text-long' : 'text-short'}>
                {t.side.toLowerCase() === 'buy' ? 'Bought' : 'Sold'} {t.quantity.toLocaleString()}
              </span>{' '}
              {t.symbol} <span className="text-fg-subtle">at {t.entry_price.toLocaleString()} · {t.strategy_id ?? 'manual'}</span>
            </>
          ),
        });
      }
      if (t.exit_time && t.exit_price != null) {
        out.push({
          at: t.exit_time,
          key: `x-${t.id}`,
          text: (
            <>
              Closed {t.symbol} <span className="text-fg-subtle">at {t.exit_price.toLocaleString()} · {t.strategy_id ?? 'manual'}</span>{' '}
              <span className={t.realized_pnl >= 0 ? 'text-long' : 'text-short'}>
                {t.realized_pnl >= 0 ? '+' : ''}
                {t.realized_pnl.toLocaleString(undefined, { maximumFractionDigits: 2 })}
              </span>
            </>
          ),
        });
      }
    }
    // Bulk actions (disarm-all, flatten-all) write one audit row per target;
    // collapse same action + same operator within a minute into one line.
    const audits = (auditQ.data?.data ?? [])
      .filter((a) => ['subscription', 'deployment', 'strategy_deployment', 'platform', 'broker_account'].includes(a.target_kind))
      .sort((x, y) => y.at.localeCompare(x.at));
    const groups: { at: string; id: string; action: string; who: string; n: number }[] = [];
    for (const a of audits) {
      const who = a.operator_display_name ?? a.operator_email ?? 'system';
      const last = groups.at(-1);
      if (last && last.action === a.action && last.who === who && Date.parse(last.at) - Date.parse(a.at) < 60_000) {
        last.n += 1;
      } else {
        groups.push({ at: a.at, id: a.id, action: a.action, who, n: 1 });
      }
    }
    for (const g of groups) {
      out.push({
        at: g.at,
        key: `a-${g.id}`,
        text: (
          <>
            {humanize(g.action.replace(/\./g, ' '))}
            {g.n > 1 ? <span className="tabular-nums"> × {g.n}</span> : null}{' '}
            <span className="text-fg-subtle">by {g.who}</span>
          </>
        ),
      });
    }
    return out.sort((x, y) => y.at.localeCompare(x.at)).slice(0, 18);
  }, [tradesQ.data, auditQ.data]);

  return (
    <PageContainer>
      <PageHeader title="Control center" />

      <StatStrip className="mb-6">
        <Stat
          label="Fleet"
          loading={depQ.data === undefined}
          value={`${counts.run} running`}
          sub={
            <>
              {deployments.length} deployed
              {counts.halt > 0 ? <span className="text-short">, {counts.halt} halted</span> : null}
              {counts.fail > 0 ? <span className="text-short">, {counts.fail} failed</span> : null}
            </>
          }
        />
        <Stat
          label="Realized today"
          loading={fleet === null && !fleetQ.isError}
          value={
            fleetQ.isError ? (
              <span className="text-fg-subtle">Unavailable</span>
            ) : fleet && fleet.totals[0] ? (
              <Money amount={fleet.totals[0].realized_pnl} currency={fleet.totals[0].currency} colored signed />
            ) : (
              <span className="text-fg-subtle">—</span>
            )
          }
          sub={
            fleet && fleet.totals.length > 1
              ? fleet.totals.slice(1).map((t) => (
                  <span key={t.currency} className="mr-3">
                    <Money amount={t.realized_pnl} currency={t.currency} colored signed />
                  </span>
                ))
              : `${(fleet?.cards ?? []).reduce((n, c) => n + c.trades, 0)} trades`
          }
        />
        <Stat
          label="Orders since engine start"
          loading={obsQ.data === undefined}
          value={orders.toLocaleString()}
          sub={
            <>
              <span className={errors > 0 ? 'text-short' : undefined}>{errors} errors</span>
              {p95 != null ? `, p95 ${Math.round(p95)} ms` : ''}
            </>
          }
        />
        <Stat
          label="Platform"
          loading={liveOn === null}
          value={
            frozen ? (
              <span className="text-short">Frozen</span>
            ) : liveOn ? (
              <span>Live</span>
            ) : (
              <span className="text-warn">Live off</span>
            )
          }
          sub={frozen ? 'All orders blocked' : liveOn ? 'Armed strategies may trade' : 'No live orders leave the engine'}
        />
      </StatStrip>

      <Card className="mb-6 overflow-hidden p-0">
        <div className="flex items-baseline justify-between px-4 pb-2.5 pt-3.5">
          <h2 className="text-md font-semibold text-fg">Deployments</h2>
          <Link href="/strategies" className="text-[13px] text-fg-subtle hover:text-fg">
            Manage strategies
          </Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead className="border-y border-divider">
              <tr>
                <Th>Strategy</Th>
                <Th>Account</Th>
                <Th>Mode</Th>
                <Th>State</Th>
                <Th className="text-right">Today</Th>
                <Th className="text-right">Trades</Th>
                <Th className="text-right">Position</Th>
                <Th className="text-right">Loss limit</Th>
                <Th className="text-right">Since</Th>
              </tr>
            </thead>
            <tbody>
              {depQ.data === undefined ? (
                Array.from({ length: 5 }, (_, i) => (
                  <tr key={i}>
                    <td colSpan={9} className="px-4 py-1.5">
                      <Skeleton className="h-6 w-full" />
                    </td>
                  </tr>
                ))
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-10 text-center text-fg-subtle">
                    No strategies are deployed yet. Deploy one from Strategies.
                  </td>
                </tr>
              ) : (
                rows.map(({ d, state, card }) => {
                  const ccy = card?.currency ?? d.base_currency ?? null;
                  return (
                    <tr
                      key={d.id}
                      onClick={() => router.push(`/strategies/${d.id}` as never)}
                      className="cursor-pointer border-b border-divider last:border-b-0 hover:bg-surface-inset"
                    >
                      <Td>
                        <div className="font-medium text-fg">{d.strategy_code_name ?? d.strategy_id}</div>
                        <div className="text-xs text-fg-subtle">
                          {d.instrument_symbol ?? card?.universe ?? '—'} · {d.timeframe_value}
                          {d.timeframe_unit}
                        </div>
                      </Td>
                      <Td className="text-fg-muted">{d.broker_account_name ?? card?.account_name ?? '—'}</Td>
                      <Td className={d.mode === 'live' ? 'text-fg' : 'text-fg-subtle'}>{d.mode === 'live' ? 'Live' : 'Paper'}</Td>
                      <Td>
                        <span className={TONE_CLASS[state.tone]} title={state.detail}>
                          {state.label}
                        </span>
                      </Td>
                      <Td className="text-right tabular-nums">
                        {card && ccy ? <Money amount={card.realized_pnl} currency={ccy} colored signed /> : <span className="text-fg-subtle">—</span>}
                      </Td>
                      <Td className="text-right tabular-nums text-fg-muted">{card ? card.trades : '—'}</Td>
                      <Td className="text-right tabular-nums">
                        {card?.open_qty ? (
                          <span className={card.open_side?.toLowerCase() === 'buy' ? 'text-long' : 'text-short'}>
                            {card.open_side?.toLowerCase() === 'buy' ? '+' : '−'}
                            {Math.abs(card.open_qty).toLocaleString()}
                          </span>
                        ) : (
                          <span className="text-fg-subtle">Flat</span>
                        )}
                      </Td>
                      <Td className="text-right tabular-nums text-fg-muted">
                        {d.effective_loss_limit != null && ccy ? <Money amount={d.effective_loss_limit} currency={ccy} /> : '—'}
                      </Td>
                      <Td className="text-right tabular-nums text-fg-subtle">{d.status === 'running' ? ago(d.started_at) : ago(d.stopped_at)}</Td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-5">
        <Card className="overflow-hidden p-0 xl:col-span-3">
          <h2 className="px-4 pb-2.5 pt-3.5 text-md font-semibold text-fg">Activity</h2>
          <ul className="border-t border-divider">
            {tradesQ.data === undefined || auditQ.data === undefined ? (
              <li className="px-4 py-3"><Skeleton className="h-24 w-full" /></li>
            ) : events.length === 0 ? (
              <li className="px-4 py-8 text-center text-[13px] text-fg-subtle">Nothing has happened today yet.</li>
            ) : (
              events.map((e) => (
                <li key={e.key} className="flex gap-4 border-b border-divider px-4 py-2 text-[13px] last:border-b-0">
                  <span className="w-24 shrink-0 tabular-nums text-fg-subtle">
                    {e.at >= sinceIso ? fmtTime.timeShort(e.at) : `${fmtTime.monthDay(e.at)} ${fmtTime.timeShort(e.at)}`}
                  </span>
                  <span className="min-w-0 text-fg">{e.text}</span>
                </li>
              ))
            )}
          </ul>
        </Card>
        <Card className="overflow-hidden p-0 xl:col-span-2">
          <h2 className="px-4 pb-2.5 pt-3.5 text-md font-semibold text-fg">Needs attention</h2>
          <ul className="border-t border-divider">
            {alerts.length === 0 ? (
              <li className="px-4 py-8 text-center text-[13px] text-fg-subtle">Nothing needs attention.</li>
            ) : (
              alerts.map((a) => {
                const body = (
                  <span className="flex gap-3">
                    <span className={cn('mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full', a.tone === 'crit' ? 'bg-crit' : 'bg-warn')} />
                    <span className="min-w-0">{a.text}</span>
                  </span>
                );
                return (
                  <li key={a.key} className="border-b border-divider text-[13px] last:border-b-0">
                    {a.href ? (
                      <Link href={a.href as never} className="block px-4 py-2.5 hover:bg-surface-inset">{body}</Link>
                    ) : (
                      <div className="px-4 py-2.5">{body}</div>
                    )}
                  </li>
                );
              })
            )}
          </ul>
        </Card>
      </div>
    </PageContainer>
  );
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return <th className={cn('px-4 py-2 text-left text-xs font-medium text-fg-subtle', className)}>{children}</th>;
}
function Td({ children, className }: { children: React.ReactNode; className?: string }) {
  return <td className={cn('px-4 py-[7px] align-middle', className)}>{children}</td>;
}
